import Vue from "vue";
import actions from "./actions";
import {
  errorMutations,
  loadingMutations,
  replaceOrSetArrayObjectsValue,
  resetErrorsAndLoading,
  setter,
  getter,
  proxy,
  metadataSetter
} from "@/store/utils";
import {
  FETCH_TAGS_LIST,
  FETCH_TAG,
  FETCH_CONTACT_LIST,
  CREATE_CONTACT,
  UPDATE_TAGS,
  CREATE_TAG,
  UPLOAD_CSV,
  FETCH_CONTACT,
  FETCH_MAIL_PIECES,
  FETCH_CONTACT_DETAILS_TAGS_LIST,
  EDIT_CONTACT,
  DELETE_CONTACT,
  DELETE_CONTACTS_BY_TAGS,
  VALIDATE_CONTACT,
  CHANGE_DETAILS_ACTIVITY_VIEW,
  FETCH_LETTERS_PIECES
} from "./types";

import { paginationFilterSortGetters } from "@/store/common/getters";
import { sortSearchPaginateMutationsNewFormat } from "@/store/common/mutations";
import { paginationFilterSortInitialObject } from "@/store/constants";

const state = {
  tags: [],
  contactList: [],
  isLoading: false,
  mailPiecesLoading: false,
  errors: null,
  mailPieces: [],
  contactActivities: [],
  contactEditing: false,
  // view type for contact details activity
  // cards or table
  activityView: "cards",

  ...paginationFilterSortInitialObject
};

const getters = {
  // not deleted
  getLiveTags: state => state.tags.filter(tag => !tag.deleted),
  getTag: state => id => {
    const res = state.tags.filter(tag => tag.id === id);

    if (res.length) {
      return res[0];
    }
  },
  getContact: state => id =>
    state.contactList.filter(contact => contact.id === id)[0],
  isLoading: getter("isLoading"),
  contacts: getter("contactList"),
  tags: getter("tags"),
  mailPieces: getter("mailPieces"),
  contactEditing: getter("contactEditing"),
  contactActivities: getter("contactActivities"),
  activityView: getter("activityView"),
  mailPiecesLoading: getter("mailPiecesLoading"),
  identityLoading: proxy("account/identityLoading"),
  ...paginationFilterSortGetters
};

const mutations = {
  [FETCH_TAGS_LIST.success](state, { payload }) {
    setter("tags", payload)(state);
    resetErrorsAndLoading(state);
  },

  [FETCH_CONTACT_DETAILS_TAGS_LIST.success](state, { payload }) {
    setter("tags", payload)(state);
    resetErrorsAndLoading(state);
  },

  [FETCH_TAG.success](state, { payload }) {
    replaceOrSetArrayObjectsValue(state, "tags", "id", payload.id, payload);
    resetErrorsAndLoading(state);
  },

  [CHANGE_DETAILS_ACTIVITY_VIEW](state, { payload }) {
    setter("activityView", payload)(state);
  },

  [FETCH_CONTACT_LIST.success](state, { payload }) {
    setter("contactList", payload.items)(state);
    metadataSetter(payload, state, ["pagination", "sort", "filters"]);
    resetErrorsAndLoading(state);
  },

  [CREATE_CONTACT.success](state, { payload }) {
    replaceOrSetArrayObjectsValue(
      state,
      "contactList",
      "id",
      payload.id,
      payload,
      true
    );

    resetErrorsAndLoading(state);
  },

  [EDIT_CONTACT.success](state, { payload }) {
    const idx = state["contactList"].findIndex(
      field => field["id"] === payload.id
    );

    const newObj = [
      {
        ...state.contactList[idx],
        ...payload
      }
    ];

    setter("contactList", newObj)(state);
    setter("contactEditing", false)(state);
    resetErrorsAndLoading(state);
  },

  [EDIT_CONTACT.request](state) {
    setter("contactEditing", true)(state);
  },

  [EDIT_CONTACT.error](state) {
    setter("contactEditing", false)(state);
  },

  [UPDATE_TAGS](state, { payload }) {
    if (state.tags.indexOf(payload) === -1) {
      state.tags.push(payload);
    }
  },

  [CREATE_TAG.success](state, { payload }) {
    const newObj = [...state.tags, payload];
    setter("tags", newObj)(state);
    resetErrorsAndLoading(state);
  },

  [UPLOAD_CSV.success](state) {
    resetErrorsAndLoading(state);
  },

  [DELETE_CONTACT.success](state, { payload }) {
    const updList = state.contactList.filter(item => item.id !== payload);
    setter("contactList", updList)(state);
    resetErrorsAndLoading(state);
  },

  [DELETE_CONTACTS_BY_TAGS.success](state, {payload}) {
    resetErrorsAndLoading(state)
  },

  [FETCH_CONTACT.success](state, { payload }) {
    replaceOrSetArrayObjectsValue(
      state,
      "contactList",
      "id",
      payload.id,
      payload
    );
    setter("contactActivities", payload.activities)(state);
    resetErrorsAndLoading(state);
  },

  [FETCH_MAIL_PIECES.request](state) {
    setter("mailPiecesLoading", true)(state);
  },

  [FETCH_LETTERS_PIECES.request](state) {
    setter("mailPiecesLoading", true)(state);
  },

  [FETCH_MAIL_PIECES.success](state, { payload }) {
    setter("contactActivities", payload)(state);
    setter("mailPiecesLoading", false)(state);
    resetErrorsAndLoading(state);
  },

  [FETCH_LETTERS_PIECES.success](state, { payload }) {
    setter("contactActivities", payload)(state);
    setter("mailPiecesLoading", false)(state);
    resetErrorsAndLoading(state);
  },

  [FETCH_MAIL_PIECES.error](state) {
    setter("mailPiecesLoading", false)(state);
  },

  [FETCH_LETTERS_PIECES.error](state) {
    setter("mailPiecesLoading", false)(state);
  },

  [VALIDATE_CONTACT.request](state, { payload }) {},

  [VALIDATE_CONTACT.success](state, { payload }) {
    const contactIndex = state.contactList.findIndex(
      item => item.id === payload.id
    );

    if (contactIndex !== -1) {
      state.contactList[contactIndex] = {
        ...state.contactList[contactIndex],
        verification: payload.data
      };

      Vue.set(state, "contactList", [...state.contactList]);
      // Vue.set(state, "contactList", { ...state.contactList });
    }

    resetErrorsAndLoading(state);
  },

  ...loadingMutations([
    FETCH_TAGS_LIST.request,
    FETCH_TAG.request,
    FETCH_CONTACT_LIST.request,
    FETCH_CONTACT_DETAILS_TAGS_LIST.request,
    CREATE_CONTACT.request,
    UPLOAD_CSV.request,
    FETCH_CONTACT.request,
    // FETCH_MAIL_PIECES.request,
    DELETE_CONTACT.request,
    DELETE_CONTACTS_BY_TAGS.request
  ]),

  ...errorMutations([
    FETCH_TAGS_LIST.error,
    FETCH_TAG.error,
    FETCH_CONTACT_LIST.error,
    FETCH_CONTACT_DETAILS_TAGS_LIST.error,
    CREATE_CONTACT.error,
    CREATE_TAG.error,
    UPLOAD_CSV.error,
    FETCH_CONTACT.error,
    // FETCH_MAIL_PIECES.error,
    DELETE_CONTACT.error,
    VALIDATE_CONTACT.error
  ]),

  ...sortSearchPaginateMutationsNewFormat
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
